import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Sun, UtensilsCrossed, Factory, ShoppingBag, Nut, Apple, Droplet, Waves, Flame, Sparkles, GraduationCap, Home as HomeIcon, Wheat, Zap, Palmtree, Building2, Truck, Fish, Snowflake, Wind, Sprout, Download, Leaf } from "lucide-react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import { useT } from "@/i18n/helpers";
import { TESTIMONIALS } from "@/data/content";
import { useBrands, useTresors, useActualites, useImpactStats } from "@/data/hooks";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";

const HERO_IMG = "/photos/home-hero-bissau.jpg";

const Hero = () => {
 const tt = useT();
 const { scrollY } = useScroll();
 const y = useTransform(scrollY, [0, 800], [0, 160]);
 const opacity = useTransform(scrollY, [0, 500], [1, 0.6]);

 return (
 <section data-testid="home-hero" className="relative h-[100vh] min-h-[720px] w-full overflow-hidden bg-nanda-ink">
 <motion.div style={{ y }} className="absolute inset-0">
 <img
 src={HERO_IMG}
 alt={tt(
 "Vue aérienne de Bissau, capitale de la Guinée-Bissau, ouverte sur l'océan Atlantique",
 "Aerial view of Bissau, capital of Guinea-Bissau, opening onto the Atlantic Ocean",
 "Vista aérea de Bissau, capital da Guiné-Bissau, aberta sobre o oceano Atlântico"
 )}
 className="h-full w-full object-cover"
 loading="eager"
 />
 {/* Overlay 40% + dégradé latéral gauche pour la lisibilité du texte */}
 <div className="absolute inset-0 bg-black/40" />
 <div className="absolute inset-0 bg-gradient-to-r from-nanda-ink/70 via-nanda-ink/30 to-transparent" />
 <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-nanda-ink/50" />
 </motion.div>
 <motion.div
 style={{ opacity }}
 className="relative z-10 mx-auto max-w-[1400px] h-full px-6 md:px-10 lg:px-14 flex flex-col justify-center items-start pt-24 md:pt-28"
 >
 <motion.div
 initial={{ opacity: 0, y: 16 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
 className="label-overline text-white/85 mb-8"
 >
 {tt("Guinée-Bissau · Depuis 2023", "Guinea-Bissau · Since 2023", "Guiné-Bissau · Desde 2023")}
 </motion.div>
 <motion.h1
 initial={{ opacity: 0, y: 30 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 1.2, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
 className="font-serif text-white text-5xl sm:text-6xl md:text-7xl lg:text-[92px] max-w-4xl text-balance leading-[1.02]"
 >
 {tt(
 "Révéler les opportunités de la Guinée-Bissau.",
 "Revealing the opportunities of Guinea-Bissau.",
 "Revelar as oportunidades da Guiné-Bissau."
 )}
 </motion.h1>
 <motion.p
 initial={{ opacity: 0, y: 24 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
 className="mt-10 max-w-2xl text-white/90 text-base md:text-lg leading-relaxed"
 >
 {tt(
 "NANDA SÁ accompagne les investisseurs, les entreprises et les institutions dans la découverte des opportunités économiques, agricoles, énergétiques, commerciales et humaines de la Guinée-Bissau.",
 "NANDA SÁ supports investors, companies and institutions in discovering the economic, agricultural, energy, commercial and human opportunities of Guinea-Bissau.",
 "A NANDA SÁ acompanha os investidores, as empresas e as instituições na descoberta das oportunidades económicas, agrícolas, energéticas, comerciais e humanas da Guiné-Bissau."
 )}
 </motion.p>
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ duration: 1, delay: 0.55 }}
 className="mt-12 flex flex-wrap gap-4"
 >
 <Link
 to="/secteurs"
 data-testid="hero-cta-activities"
 className="btn-arrow group inline-flex items-center gap-3 bg-white text-nanda-ink px-7 py-4 hover:bg-nanda-sand transition-colors duration-500 label-overline"
 >
 {tt("Découvrir nos activités", "Discover our activities", "Descobrir as nossas actividades")}
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <Link
 to="/opportunites"
 data-testid="hero-cta-invest"
 className="btn-arrow inline-flex items-center gap-3 bg-transparent text-white border border-white/50 px-7 py-4 hover:bg-white hover:text-nanda-ink transition-colors duration-500 label-overline"
 >
 {tt("Investir avec nous", "Invest with us", "Investir connosco")}
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </motion.div>
 </motion.div>
 </section>
 );
};

const B2BPillars = () => {
 const tt = useT();
 const pillars = [
 {
 to: "/refrigeration-climatisation",
 icon: Snowflake,
 fr: "Réfrigération & climatisation", en: "Refrigeration & AC", pt: "Refrigeração e climatização",
 fr_d: "Chambres froides, chaîne du froid industrielle, climatisation split et centralisée, maintenance et SAV.",
 en_d: "Cold rooms, industrial cold chain, split and centralised air conditioning, maintenance and after-sales.",
 pt_d: "Câmaras frias, cadeia de frio industrial, climatização split e centralizada, manutenção e SAV.",
 },
 {
 to: "/energies-renouvelables",
 icon: Sun,
 fr: "Énergies renouvelables", en: "Renewable energy", pt: "Energias renováveis",
 fr_d: "Solaire résidentiel, tertiaire, industriel, pompage, éclairage public, stockage lithium et backup.",
 en_d: "Residential, commercial, industrial solar, pumping, street lighting, lithium storage and backup.",
 pt_d: "Solar residencial, terciário, industrial, bombagem, iluminação pública, armazenamento e backup.",
 },
 {
 to: "/conseil-implantation",
 icon: Building2,
 fr: "Conseil & implantation", en: "Consulting & establishment", pt: "Consultoria e implantação",
 fr_d: "Étude de marché, terrains, administratif, partenaires locaux, gestion de projet, recrutement et logistique.",
 en_d: "Market study, land, administrative, local partners, project management, recruitment and logistics.",
 pt_d: "Estudo de mercado, terrenos, administrativo, parceiros locais, gestão de projecto, recrutamento e logística.",
 },
 {
 to: "/matieres-premieres-naturelles",
 icon: Leaf,
 fr: "Matières premières naturelles", en: "Natural raw materials", pt: "Matérias-primas naturais",
 fr_d: "Sourcing, contrôle qualité, valorisation et mise en relation autour des matières agricoles et botaniques.",
 en_d: "Sourcing, quality control, valorisation and matching around agricultural and botanical raw materials.",
 pt_d: "Sourcing, controlo de qualidade, valorização e mise en relation em matérias agrícolas e botânicas.",
 },
 {
 to: "/filiere-cajou",
 icon: Nut,
 fr: "Filière cajou", en: "Cashew value chain", pt: "Fileira do caju",
 fr_d: "Sourcing, valorisation, accompagnement de la transformation locale et développement de débouchés à l'export.",
 en_d: "Sourcing, valorisation, support for local processing and development of export outlets.",
 pt_d: "Sourcing, valorização, acompanhamento da transformação local e desenvolvimento de saídas para exportação.",
 },
 {
 to: "/marques/academie",
 icon: GraduationCap,
 fr: "NANDA SÁ Académie", en: "NANDA SÁ Académie", pt: "NANDA SÁ Académie",
 fr_d: "Éducation, orientation, culture numérique, lecture, découverte des métiers, entrepreneuriat et leadership.",
 en_d: "Education, guidance, digital literacy, reading, career discovery, entrepreneurship and leadership.",
 pt_d: "Educação, orientação, cultura digital, leitura, descoberta de profissões, empreendedorismo e liderança.",
 },
 ];
 return (
 <section data-testid="home-b2b-pillars" className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-end mb-16 md:mb-20">
 <FadeUp className="md:col-span-5">
 <div className="label-overline text-nanda-terracotta mb-6">
 {tt("Nos solutions professionnelles", "Our professional solutions", "As nossas soluções profissionais")}
 </div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-nanda-ink">
 {tt(
 "Une entreprise B2B ancrée en Afrique de l'Ouest.",
 "A B2B company rooted in West Africa.",
 "Uma empresa B2B enraizada na África Ocidental."
 )}
 </h2>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-7">
 <p className="text-nanda-inkSoft text-[16px] md:text-[17px] leading-[1.75] max-w-2xl">
 {tt(
 "Réfrigération, climatisation, énergie solaire, conseil, matières premières et filière cajou : nos activités professionnelles s'adressent aux entreprises, aux investisseurs et aux institutions qui construisent en Guinée-Bissau et à l'international.",
 "Refrigeration, air conditioning, solar energy, consulting, raw materials and cashew value chain: our professional activities serve companies, investors and institutions building in Guinea-Bissau and internationally.",
 "Refrigeração, climatização, energia solar, consultoria, matérias-primas e fileira do caju: as nossas actividades profissionais dirigem-se a empresas, investidores e instituições que constroem na Guiné-Bissau e a nível internacional."
 )}
 </p>
 </FadeUp>
 </div>

 <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {pillars.map((p, i) => {
 const Icon = p.icon;
 return (
 <StaggerItem key={p.to}>
 <Link
 to={p.to}
 data-testid={`b2b-pillar-${i}`}
 className="bg-nanda-bg p-8 md:p-10 h-full flex flex-col hover:bg-nanda-bgAlt transition-colors duration-500 group"
 >
 <Icon size={28} strokeWidth={1.2} className="text-nanda-terracotta mb-6 group-hover:scale-110 transition-transform duration-500" />
 <div className="font-serif text-xl md:text-2xl text-nanda-ink mb-3 leading-tight flex items-baseline gap-2">
 {tt(p.fr, p.en, p.pt)}
 <ArrowUpRight size={16} strokeWidth={1.5} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
 </div>
 <p className="text-nanda-inkSoft text-[15px] leading-[1.7]">
 {tt(p.fr_d, p.en_d, p.pt_d)}
 </p>
 </Link>
 </StaggerItem>
 );
 })}
 </Stagger>

 <FadeUp delay={0.3} className="mt-14 flex flex-wrap gap-4">
 <Link
 to="/devis"
 data-testid="b2b-cta-devis"
 className="btn-arrow inline-flex items-center gap-3 bg-nanda-ink text-white px-8 py-4 hover:bg-nanda-terracotta transition-colors duration-500 label-overline"
 >
 {tt("Demander un devis", "Request a quote", "Solicitar orçamento")}
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <a
 href="/pdf/nanda-sa-note-energie.pdf"
 download
 data-testid="b2b-cta-pdf-energie"
 className="btn-arrow inline-flex items-center gap-3 border border-nanda-line text-nanda-ink px-8 py-4 hover:bg-nanda-bgAlt transition-colors duration-500 label-overline"
 >
 {tt("Télécharger la note énergie", "Download the energy brief", "Descarregar a nota energia")}
 <Download size={16} strokeWidth={1.5} />
 </a>
 </FadeUp>
 </div>
 </section>
 );
};

const OpportunitesGB = () => {
 const tt = useT();
 const sectors = [
 {
 icon: Wheat,
 fr: "Agriculture", en: "Agriculture", pt: "Agricultura",
 fr_d: "Cajou, mangue, riz, agrumes, maraîchage : une productivité rare, encore peu transformée localement.",
 en_d: "Cashew, mango, rice, citrus, market gardening: rare productivity, still lightly processed locally.",
 pt_d: "Caju, manga, arroz, citrinos, horticultura: uma produtividade rara, ainda pouco transformada localmente.",
 },
 {
 icon: Zap,
 fr: "Énergie", en: "Energy", pt: "Energia",
 fr_d: "2 500 à 3 000 heures d'ensoleillement annuel : l'un des potentiels solaires les plus élevés d'Afrique de l'Ouest.",
 en_d: "2,500 to 3,000 hours of annual sunshine: one of the highest solar potentials in West Africa.",
 pt_d: "2 500 a 3 000 horas de insolação anual: um dos maiores potenciais solares da África Ocidental.",
 },
 {
 icon: Palmtree,
 fr: "Tourisme", en: "Tourism", pt: "Turismo",
 fr_d: "L'archipel des Bijagós, la culture Balante, la Guinée-Bissau voisine : un tourisme d'expérience à structurer.",
 en_d: "The Bijagós archipelago, Balante culture, neighbouring Guinée-Bissau: an experiential tourism to structure.",
 pt_d: "O arquipélago dos Bijagós, a cultura Balanta, a Guiné-Bissau vizinha: um turismo de experiência a estruturar.",
 },
 {
 icon: Building2,
 fr: "Immobilier", en: "Real estate", pt: "Imobiliário",
 fr_d: "Commerces, entrepôts logistiques, logements, hôtellerie : des marchés urbains émergents à Bissau et Canchungo.",
 en_d: "Retail, logistics warehouses, housing, hospitality: emerging urban markets in Bissau and Canchungo.",
 pt_d: "Comércios, armazéns logísticos, habitação, hotelaria: mercados urbanos emergentes em Bissau e Canchungo.",
 },
 {
 icon: Truck,
 fr: "Logistique", en: "Logistics", pt: "Logística",
 fr_d: "Corridor Bissau–port de ouverture CEDEAO : un carrefour transfrontalier stratégique.",
 en_d: "Bissau–corridor, port of ECOWAS opening: a strategic cross-border crossroads.",
 pt_d: "Corredor Bissau–porto de Bissau, abertura CEDEAO: um cruzamento transfronteiriço estratégico.",
 },
 {
 icon: Factory,
 fr: "Transformation agroalimentaire", en: "Agri-food processing", pt: "Transformação agroalimentar",
 fr_d: "80 à 90 % du cajou quitte encore le pays brut : la valeur ajoutée locale reste à construire.",
 en_d: "80 to 90% of cashew still leaves the country raw: local added value is yet to be built.",
 pt_d: "80 a 90% do caju ainda sai do país em bruto: o valor acrescentado local está por construir.",
 },
 {
 icon: Fish,
 fr: "Économie bleue", en: "Blue economy", pt: "Economia azul",
 fr_d: "350 km de côte, mangroves, pêche artisanale et transformation : une richesse maritime encore inexploitée.",
 en_d: "350 km of coastline, mangroves, artisanal fishing and processing: a maritime wealth still untapped.",
 pt_d: "350 km de costa, mangais, pesca artesanal e transformação: uma riqueza marítima ainda por explorar.",
 },
 ];
 return (
 <section data-testid="home-opportunites-gb" className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp>
 <div className="label-overline text-nanda-terracotta mb-8">
 {tt("Panorama · Investir en Guinée-Bissau", "Panorama · Invest in Guinea-Bissau", "Panorama · Investir na Guiné-Bissau")}
 </div>
 </FadeUp>
 <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-end mb-16">
 <FadeUp delay={0.05} className="md:col-span-7">
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-nanda-ink">
 {tt(
 "Les opportunités de la Guinée-Bissau.",
 "The opportunities of Guinea-Bissau.",
 "As oportunidades da Guiné-Bissau."
 )}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-5">
 <p className="text-nanda-inkSoft text-[16px] md:text-[17px] leading-[1.75]">
 {tt(
 "Un territoire d'avenir de part et d'autre du fleuve Cacheu : agriculture, énergie, tourisme, immobilier, transformation agroalimentaire, logistique et innovation. NANDA SÁ met en lumière les opportunités qui façonnent ce carrefour stratégique d'Afrique de l'Ouest.",
 "A land of the future on both sides of the Cacheu river: agriculture, energy, tourism, real estate, agri-food processing, logistics and innovation. NANDA SÁ highlights the opportunities shaping this strategic West African crossroads.",
 "Um território de futuro dos dois lados do rio Cacheu: agricultura, energia, turismo, imobiliário, transformação agroalimentar, logística e inovação. A NANDA SÁ destaca as oportunidades que moldam este cruzamento estratégico da África Ocidental."
 )}
 </p>
 </FadeUp>
 </div>

 <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {sectors.map((s, i) => {
 const Icon = s.icon;
 return (
 <StaggerItem key={i}>
 <div
 data-testid={`opp-sector-${i}`}
 className="bg-nanda-bg p-8 md:p-10 h-full flex flex-col hover:bg-nanda-sand/40 transition-colors duration-500"
 >
 <Icon size={28} strokeWidth={1.2} className="text-nanda-terracotta mb-6" />
 <div className="font-serif text-xl md:text-2xl text-nanda-ink mb-3 leading-tight">
 {tt(s.fr, s.en, s.pt)}
 </div>
 <p className="text-nanda-inkSoft text-[15px] leading-[1.7]">
 {tt(s.fr_d, s.en_d, s.pt_d)}
 </p>
 </div>
 </StaggerItem>
 );
 })}
 {/* Cellule CTA finale, occupe la 8e case pour compléter la grille */}
 <StaggerItem>
 <Link
 to="/opportunites"
 data-testid="opp-cta-full"
 className="bg-nanda-ink text-white p-8 md:p-10 h-full flex flex-col justify-between hover:bg-nanda-terracotta transition-colors duration-500 group"
 >
 <div>
 <div className="label-overline text-white/70 mb-6">
 {tt("Le panorama complet", "The full panorama", "O panorama completo")}
 </div>
 <div className="font-serif text-xl md:text-2xl leading-tight">
 {tt(
 "Découvrir les opportunités",
 "Discover the opportunities",
 "Descobrir as oportunidades"
 )}
 </div>
 </div>
 <ArrowUpRight size={22} strokeWidth={1.3} className="mt-8 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" />
 </Link>
 </StaggerItem>
 </Stagger>

 <FadeUp delay={0.3} className="mt-14">
 <Link
 to="/opportunites"
 data-testid="opp-cta-primary"
 className="btn-arrow inline-flex items-center gap-3 bg-nanda-ink text-white px-8 py-4 hover:bg-nanda-terracotta transition-colors duration-500 label-overline"
 >
 {tt("Découvrir les opportunités", "Discover the opportunities", "Descobrir as oportunidades")}
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 </div>
 </section>
 );
};

const Manifeste = () => {
 const tt = useT();
 return (
 <section data-testid="home-manifeste" className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1200px] px-6 md:px-10 lg:px-14 py-28 md:py-40">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-10">{tt("Le Manifeste", "The Manifesto", "O Manifesto")}</div>
 </FadeUp>
 <FadeUp delay={0.1}>
 <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-balance leading-[1.02] text-nanda-ink">
 {tt("Une entreprise, oui.", "A company, yes.", "Uma empresa, sim.")}<br /><span className="text-nanda-inkSoft">{tt("Mais surtout, quelque chose à laisser derrière.", "But above all, something worth leaving behind.", "Mas, sobretudo, algo que vale a pena deixar para trás.")}</span>
 </h2>
 </FadeUp>
 <FadeUp delay={0.2} className="mt-16 max-w-3xl space-y-6 text-nanda-inkSoft text-[17px] md:text-lg leading-relaxed">
 <p>{tt(
 "Chez nous, une entreprise ne se réduit pas à la somme de ses activités. Nous voyons plus loin. Il s'agit de transmettre une vision, de protéger des savoir-faire qui pourraient disparaître, d'ouvrir des perspectives à celles et ceux qui construisent. Il s'agit aussi, plus discrètement, d'ancrer tout cela dans un territoire, et de préparer ce qui viendra après nous.",
 "For us, a company is not the sum of its activities. We look further. It is about passing on a vision, protecting know-how that might otherwise fade, opening doors for the people who build. And more quietly, it is about rooting all of this in a territory, and preparing what will come after us.",
 "Para nós, uma empresa não é a soma das suas actividades. Olhamos mais longe. Trata-se de transmitir uma visão, de proteger saberes que poderiam desaparecer, de abrir caminhos para quem constrói. E, mais discretamente, de enraizar tudo isto num território, e de preparar o que virá depois de nós."
 )}</p>
 <p>{tt(
 "Chaque projet naît de cette conviction : les richesses de l'Afrique de l'Ouest méritent d'être révélées avec ambition. Sans naïveté, sans folklore, sans concession sur la qualité.",
 "Every project starts from this conviction: the wealth of West Africa deserves to be revealed with ambition. Without naivety, without folklore, without cutting corners on quality.",
 "Cada projecto nasce desta convicção: as riquezas da África Ocidental merecem ser reveladas com ambição. Sem ingenuidade, sem folclore, sem cedências na qualidade."
 )}</p>
 </FadeUp>
 <FadeUp delay={0.3} className="mt-12">
 <Link to="/maison" data-testid="manifeste-link" className="btn-arrow inline-flex items-center gap-3 text-nanda-ink label-overline border-b border-nanda-ink pb-1 hover:text-nanda-terracotta hover:border-nanda-terracotta transition-colors duration-500">
 {tt("Lire le manifeste complet", "Read the full manifesto", "Ler o manifesto completo")}
 <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 </div>
 </section>
 );
};

const VisionMission = () => {
 const tt = useT();
 return (
 <section data-testid="home-vision" className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-14">
 <div className="label-overline text-nanda-muted mb-4">{tt("Notre cap", "Where we're headed", "O nosso rumo")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl">{tt("Ce que nous cherchons, comment nous travaillons, ce à quoi nous tenons.", "What we aim for, how we work, what we hold on to.", "O que procuramos, como trabalhamos, aquilo a que nos apegamos.")}</h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 <StaggerItem className="bg-nanda-bg p-10 md:p-14" data-testid="vision-card">
 <div className="label-overline text-nanda-muted">{tt("Là où nous allons", "Where we're going", "Para onde vamos")}</div>
 <h3 className="font-serif text-3xl md:text-4xl mt-5 leading-tight">{tt("Faire compter les marques africaines, sur leur continent et ailleurs.", "Making African brands count, on their continent and beyond.", "Fazer com que as marcas africanas contem, no seu continente e mais além.")}</h3>
 <p className="mt-8 text-nanda-inkSoft text-[15px] md:text-base leading-relaxed">{tt("Notre horizon est simple à dire, moins simple à tenir : bâtir un groupe qu'on cite lorsqu'on parle d'Afrique de l'Ouest, non pour ce qu'il produit vite, mais pour ce qu'il garde longtemps.", "Our horizon is simple to say, less simple to hold: to build a group people mention when they speak of West Africa, not for what it makes fast, but for what it keeps over time.", "O nosso horizonte é simples de dizer, menos simples de manter: construir um grupo que se cita quando se fala da África Ocidental, não pelo que faz depressa, mas pelo que preserva ao longo do tempo.")}</p>
 </StaggerItem>
 <StaggerItem className="bg-nanda-bg p-10 md:p-14" data-testid="mission-card">
 <div className="label-overline text-nanda-muted">{tt("Ce que nous faisons", "What we do", "O que fazemos")}</div>
 <h3 className="font-serif text-3xl md:text-4xl mt-5 leading-tight">{tt("Faire les choses avec sérieux, et pour durer.", "Doing things properly, and for the long haul.", "Fazer as coisas com seriedade, e para durar.")}</h3>
 <p className="mt-8 text-nanda-inkSoft text-[15px] md:text-base leading-relaxed">{tt("Nous travaillons dans des secteurs qui touchent au quotidien : produits naturels, cosmétiques botaniques, énergie solaire, équipements professionnels, et bientôt la formation et l'art de vivre. À chaque fois, la même exigence.", "We work in sectors that touch daily life: natural products, botanical cosmetics, solar energy, professional equipment, and soon training and lifestyle. Every time, the same standard.", "Trabalhamos em sectores que tocam o dia-a-dia: produtos naturais, cosméticos botânicos, energia solar, equipamentos profissionais e, em breve, formação e arte de viver. De cada vez, a mesma exigência.")}</p>
 </StaggerItem>
 <StaggerItem className="bg-nanda-bg p-10 md:p-14" data-testid="values-card">
 <div className="label-overline text-nanda-muted">{tt("Ce qui nous tient", "What holds us", "O que nos mantém")}</div>
 <ul className="mt-5 space-y-4">
 {[
 tt("Le travail bien fait", "Work done properly", "O trabalho bem feito"),
 tt("Ce qui vient d'ici", "What comes from here", "O que vem daqui"),
 tt("La curiosité, pas la mode", "Curiosity, not fashion", "A curiosidade, não a moda"),
 tt("Ce qu'on laisse derrière", "What we leave behind", "O que se deixa para trás"),
 tt("Passer à celles et ceux d'après", "Passing on to those who come next", "Passar aos que vêm a seguir"),
 ].map((v) => (
 <li key={v} className="flex items-baseline gap-3">
 <span className="h-px w-6 bg-nanda-ink mt-2" />
 <span className="font-serif text-2xl md:text-3xl text-nanda-ink">{v}</span>
 </li>
 ))}
 </ul>
 </StaggerItem>
 </Stagger>
 </div>
 </section>
 );
};

const Houses = () => {
 const BRANDS = useBrands();
 const tt = useT();
 return (
 <section data-testid="home-brands" className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 md:mb-20">
 <FadeUp className="md:col-span-4">
 <div className="label-overline text-nanda-muted">{tt("Les quatre Maisons", "The Four Houses", "As quatro Casas")}</div>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-8">
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-balance leading-[1.05]">
 {tt("Quatre Maisons. Une même exigence.", "Four Houses. One same rigour.", "Quatro Casas. Uma mesma exigência.")}
 </h2>
 </FadeUp>
 </div>

 <Stagger className="space-y-px bg-nanda-line border border-nanda-line">
 {BRANDS.map((b, i) => (
 <StaggerItem key={b.slug} className="bg-nanda-bg">
 <Link to={`/marques/${b.slug}`} data-testid={`brand-row-${b.slug}`} className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center p-6 md:p-10 hover:bg-nanda-bgAlt transition-colors duration-500">
 <div className={`md:col-span-4 aspect-[5/4] md:aspect-[5/3] overflow-hidden ${b.heroStyle === "product" ? "bg-[#F5EDE2] flex items-center justify-center" : ""}`}>
 <img src={b.image} alt={b.name} loading="lazy" className={`h-full w-full transition-transform duration-[1200ms] ease-out group-hover:scale-105 ${b.heroStyle === "product" ? "object-contain p-4" : "object-cover"}`} />
 </div>
 <div className="md:col-span-1 label-overline text-nanda-muted">0{i + 1}</div>
 <div className="md:col-span-5">
 <div className="font-serif text-3xl md:text-4xl lg:text-5xl text-nanda-ink leading-tight">{b.name}</div>
 <div className="label-overline text-nanda-muted mt-3">{b.category}</div>
 <p className="mt-5 text-nanda-inkSoft text-[15px] md:text-base max-w-md">{b.tagline}</p>
 </div>
 <div className="md:col-span-2 md:text-right">
 <span className="inline-flex items-center gap-2 label-overline text-nanda-ink group-hover:translate-x-1 transition-transform duration-500">
 {tt("Découvrir", "Discover", "Descobrir")} <ArrowUpRight size={14} strokeWidth={1.5} />
 </span>
 </div>
 </Link>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 );
};

const Counter = ({ value, suffix }) => {
 const ref = useRef(null);
 const inView = useInView(ref, { once: true, amount: 0.4 });
 const [n, setN] = useState(0);

 useEffect(() => {
 if (!inView) return;
 const duration = 1600;
 const start = performance.now();
 let raf;
 const tick = (t) => {
 const p = Math.min(1, (t - start) / duration);
 const eased = 1 - Math.pow(1 - p, 3);
 setN(Math.round(eased * value));
 if (p < 1) raf = requestAnimationFrame(tick);
 };
 raf = requestAnimationFrame(tick);
 return () => cancelAnimationFrame(raf);
 }, [inView, value]);

 return (
 <span ref={ref} className="font-serif text-6xl md:text-7xl lg:text-8xl text-nanda-ink tabular-nums">
 {n}
 <span className="text-nanda-terracotta">{suffix}</span>
 </span>
 );
};

const Stats = () => {
 const IMPACT_STATS = useImpactStats();
 const tt = useT();
 return (
 <section data-testid="home-stats" className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-14 md:mb-20">
 <div className="label-overline text-nanda-muted mb-4">{tt("Chiffres clés", "Key figures", "Números-chave")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl">{tt("L'empreinte du Groupe", "The Group's footprint", "A pegada do Grupo")}</h2>
 </FadeUp>
 <div className="grid grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-16">
 {IMPACT_STATS.map((s, i) => (
 <FadeUp key={i} delay={i * 0.06}>
 <Counter value={s.value} suffix={s.suffix} />
 <div className="label-overline text-nanda-muted mt-4">{s.label}</div>
 </FadeUp>
 ))}
 </div>
 </div>
 </section>
 );
};

const Tresors = () => {
 const TRESORS = useTresors();
 const tt = useT();
 const preview = TRESORS.slice(0, 6);
 return (
 <section data-testid="home-tresors" className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 md:mb-20">
 <FadeUp className="md:col-span-5">
 <div className="label-overline text-nanda-muted mb-4">{tt("Les Trésors", "The Treasures", "Os Tesouros")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02]">{tt("Les Trésors d'Afrique de l'Ouest.", "The Treasures of West Africa.", "Os Tesouros da África Ocidental.")}</h2>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-6 md:col-start-7">
 <p className="text-nanda-inkSoft text-base md:text-lg leading-relaxed max-w-xl">
 {tt(
 "Du baobab au moringa, de l'hibiscus à la fleur de sel. Dix matières que nous avons choisi de suivre de près, depuis la parcelle jusqu'au produit fini.",
 "From baobab to moringa, from hibiscus to fleur de sel. Ten materials we chose to follow closely, from the field to the finished product.",
 "Do baobá à moringa, do hibisco à flor de sal. Dez matérias-primas que decidimos acompanhar de perto, do campo ao produto final."
 )}
 </p>
 <Link to="/tresors" data-testid="tresors-link" className="btn-arrow mt-6 inline-flex items-center gap-3 text-nanda-ink label-overline border-b border-nanda-ink pb-1 hover:text-nanda-terracotta hover:border-nanda-terracotta transition-colors duration-500">
 {tt("Explorer les 10 trésors", "Explore the 10 treasures", "Explorar os 10 tesouros")} <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 </div>

 <Stagger className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
 {preview.map((t) => (
 <StaggerItem key={t.slug}>
 <Link to={`/tresors/${t.slug}`} data-testid={`tresor-card-${t.slug}`} className="group block relative aspect-[4/5] overflow-hidden border border-nanda-line">
 <img src={t.image} alt={t.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
 <div className="absolute inset-0 bg-gradient-to-t from-nanda-ink/75 via-nanda-ink/10 to-transparent" />
 <div className="absolute inset-0 p-5 md:p-7 flex flex-col justify-end">
 <div className="label-overline text-white/75 mb-2">{t.territory}</div>
 <div className="font-serif text-white text-2xl md:text-3xl">{t.name}</div>
 </div>
 </Link>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 );
};

const Engagements = () => {
 const tt = useT();
 const items = [
 { label: tt("Qualité", "Quality", "Qualidade"), body: tt("Une exigence sans compromis sur chaque produit.", "Uncompromising standards on every product.", "Uma exigência sem compromissos em cada produto.") },
 { label: tt("Production responsable", "Responsible production", "Produção responsável"), body: tt("Filières tracées, producteurs respectés.", "Traceable supply chains, respected producers.", "Fileiras rastreadas, produtores respeitados.") },
 { label: tt("Circuits courts", "Short supply chains", "Circuitos curtos"), body: tt("Lien direct avec les coopératives partenaires.", "Direct link with partner cooperatives.", "Ligação directa com as cooperativas parceiras.") },
 { label: tt("Valorisation des producteurs", "Valuing producers", "Valorização dos produtores"), body: tt("Une économie locale, juste et durable.", "A local, fair and sustainable economy.", "Uma economia local, justa e duradoura.") },
 { label: tt("Innovation", "Innovation", "Inovação"), body: tt("Des solutions concrètes pour aujourd'hui et demain.", "Concrete solutions for today and tomorrow.", "Soluções concretas para hoje e amanhã.") },
 { label: tt("Développement durable", "Sustainable development", "Desenvolvimento sustentável"), body: tt("Au service des générations futures.", "In service of future generations.", "Ao serviço das gerações futuras.") },
 ];
 return (
 <section data-testid="home-engagements" className="border-t border-nanda-line bg-nanda-ink text-nanda-bg">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16">
 <div className="label-overline text-nanda-bg/60 mb-4">{tt("Nos engagements", "Our commitments", "Os nossos compromissos")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-nanda-bg leading-[1.05]">{tt("Six engagements concrets.", "Six concrete commitments.", "Seis compromissos concretos.")}</h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
 {items.map((it, i) => (
 <StaggerItem key={i} className="flex flex-col gap-3" data-testid={`engagement-${i}`}>
 <Check size={20} strokeWidth={1.5} className="text-nanda-bg/70" />
 <div className="font-serif text-2xl md:text-3xl text-nanda-bg">{it.label}</div>
 <div className="text-nanda-bg/65 text-[15px] leading-relaxed max-w-xs">{it.body}</div>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 );
};

const Why = () => {
 const tt = useT();
 const items = [
 { label: tt("Ancrage ouest-africain", "West African anchoring", "Enraizamento oeste-africano"), detail: tt("Nées entre Guinée-Bissau. Nos équipes sont locales.", "Born between Guinea-Bissau. Our teams are local.", "Nascidas entre a Guiné-Bissau. As nossas equipas são locais.") },
 { label: tt("Quatre Maisons diversifiées", "Four diversified Houses", "Quatro Casas diversificadas"), detail: tt("Un modèle résilient qui traverse les cycles économiques.", "A resilient model that crosses economic cycles.", "Um modelo resiliente que atravessa os ciclos económicos.") },
 { label: tt("Exigence sans compromis", "Uncompromising standards", "Exigência sem compromissos"), detail: tt("Qualité premium sur chaque produit, chaque installation, chaque geste.", "Premium quality on every product, every installation, every gesture.", "Qualidade premium em cada produto, cada instalação, cada gesto.") },
 { label: tt("Filières tracées", "Traceable supply chains", "Fileiras rastreáveis"), detail: tt("Producteurs identifiés, terroirs précis, récoltes datées.", "Identified producers, precise terroirs, dated harvests.", "Produtores identificados, terroirs precisos, colheitas datadas.") },
 { label: tt("Vision long terme", "Long-term vision", "Visão de longo prazo"), detail: tt("Fondée en 2023, pensée sur plusieurs décennies.", "Founded in 2023, designed over several decades.", "Fundada em 2023, pensada ao longo de várias décadas.") },
 { label: tt("Trilingue par nature", "Trilingual by nature", "Trilingue por natureza"), detail: tt("FR · EN · PT, un dialogue naturel avec le monde.", "FR · EN · PT, a natural dialogue with the world.", "FR · EN · PT, um diálogo natural com o mundo.") },
 ];
 return (
 <section data-testid="home-why" className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
 <FadeUp className="md:col-span-5">
 <div className="label-overline text-nanda-muted mb-4">{tt("Pourquoi NANDA SÁ ?", "Why NANDA SÁ?", "Porquê a NANDA SÁ?")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">{tt("Six raisons de nous choisir.", "Six reasons to choose us.", "Seis razões para nos escolher.")}</h2>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-7">
 <p className="text-nanda-inkSoft text-base md:text-lg leading-relaxed max-w-2xl">
 {tt(
 "Nous ne créons pas seulement des produits. Nous imaginons des solutions, développons des entreprises et construisons des marques capables de générer un impact économique, social et environnemental durable.",
 "We do not merely create products. We imagine solutions, grow companies and build brands capable of generating a lasting economic, social and environmental impact.",
 "Não criamos apenas produtos. Imaginamos soluções, desenvolvemos empresas e construímos marcas capazes de gerar um impacto económico, social e ambiental duradouro."
 )}
 </p>
 </FadeUp>
 </div>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {items.map((it, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-8 md:p-10" data-testid={`why-${i}`}>
 <div className="label-overline text-nanda-terracotta num-editorial mb-4">0{i + 1}</div>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink mb-4">{it.label}</h3>
 <p className="text-nanda-inkSoft text-[15px] leading-relaxed">{it.detail}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 );
};

const Carnet = () => {
 const ACTUALITES = useActualites();
 const { locale } = useLanguage();
 const tt = useT();
 const dateLocale = locale === "en" ? "en-GB" : locale === "pt" ? "pt-PT" : "fr-FR";
 return (
 <section data-testid="home-carnet" className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <div className="flex items-end justify-between mb-14">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-4">{tt("Le Carnet", "The Journal", "O Caderno")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl">{tt("Le Carnet NANDA SÁ.", "The NANDA SÁ Journal.", "O Caderno NANDA SÁ.")}</h2>
 </FadeUp>
 <FadeUp delay={0.1}>
 <Link to="/carnet" data-testid="carnet-link" className="btn-arrow hidden md:inline-flex items-center gap-3 label-overline border-b border-nanda-ink pb-1 hover:text-nanda-terracotta hover:border-nanda-terracotta transition-colors duration-500">
 {tt("Tout le Carnet", "The full Journal", "Todo o Caderno")} <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 </div>
 <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
 {ACTUALITES.slice(0, 3).map((a) => (
 <StaggerItem key={a.slug}>
 <Link to={`/carnet`} data-testid={`carnet-card-${a.slug}`} className="group block">
 <div className="aspect-[4/3] overflow-hidden mb-5">
 <img src={a.cover} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
 </div>
 <div className="label-overline text-nanda-muted">{a.type} · {new Date(a.date).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" })}</div>
 <h3 className="font-serif text-2xl md:text-3xl mt-3 leading-tight text-nanda-ink group-hover:text-nanda-terracotta transition-colors duration-500">{a.title}</h3>
 <p className="mt-3 text-nanda-inkSoft text-[15px]">{a.excerpt}</p>
 </Link>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 );
};

const Testimonials = () => {
 const tt = useT();
 if (!TESTIMONIALS || TESTIMONIALS.length === 0) return null;
 return (
 <section data-testid="home-testimonials" className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-14">
 <div className="label-overline text-nanda-muted mb-4">{tt("Témoignages", "Testimonials", "Testemunhos")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl">{tt("Ils nous accompagnent.", "They walk with us.", "Eles caminham connosco.")}</h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {TESTIMONIALS.map((t, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-10 md:p-12" data-testid={`testimonial-${i}`}>
 <div className="font-serif text-5xl text-nanda-terracotta leading-none">«</div>
 <p className="mt-6 font-serif text-2xl leading-snug text-nanda-ink">{t.quote}</p>
 <div className="mt-10 label-overline text-nanda-muted">{t.author}</div>
 <div className="mt-1 text-sm text-nanda-inkSoft">{t.role}, {t.location}</div>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 );
};

const B2BQuickAccess = () => {
 const tt = useT();
 const items = [
 { to: "/contact?subject=Devis", label: tt("Demander un devis", "Request a quote", "Solicitar orçamento"), body: tt("Énergie solaire, projets B2B, études personnalisées.", "Solar energy, B2B projects, bespoke studies.", "Energia solar, projectos B2B, estudos personalizados.") },
 { to: "/partenaires", label: tt("Devenir distributeur", "Become a distributor", "Tornar-se distribuidor"), body: tt("Rejoindre le réseau NANDA SÁ Cosmétiques ou NANDA SÁ.", "Join the NANDA SÁ Cosmétiques or NANDA SÁ network.", "Junte-se à rede NANDA SÁ Cosmétiques ou NANDA SÁ.") },
 { to: "/contact?subject=Rendez-vous", label: tt("Prendre rendez-vous", "Book an appointment", "Marcar reunião"), body: tt("Canchungo ou en visioconférence.", "Canchungo or by video call.", "Canchungo ou por videochamada.") },
 { to: "/contact?subject=Catalogue", label: tt("Télécharger le catalogue", "Download the catalogue", "Descarregar o catálogo"), body: tt("Catalogues professionnels sur demande.", "Professional catalogues on request.", "Catálogos profissionais a pedido.") },
 ];
 return (
 <section data-testid="home-b2b" className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-14 md:mb-16 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{tt("Espace professionnels", "Professional space", "Espaço profissionais")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Vous êtes une entreprise ?", "Are you a business?", "É uma empresa?")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {items.map((it, i) => (
 <StaggerItem key={i} className="bg-nanda-bg" data-testid={`b2b-quick-${i}`}>
 <Link to={it.to} className="block p-8 md:p-10 h-full group hover:bg-nanda-bgAlt transition-colors duration-500 btn-arrow">
 <div className="label-overline text-nanda-terracotta num-editorial mb-6">0{i + 1}</div>
 <h3 className="font-serif text-xl md:text-2xl leading-tight text-nanda-ink mb-4 flex items-baseline gap-3">
 {it.label}
 <ArrowUpRight size={16} strokeWidth={1.5} className="shrink-0" />
 </h3>
 <p className="text-nanda-inkSoft text-[14px] leading-relaxed">{it.body}</p>
 </Link>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 );
};

const CTA = () => {
 const tt = useT();
 return (
 <section data-testid="home-cta" className="border-t border-nanda-line bg-nanda-ink text-nanda-bg">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-7">
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-balance leading-[1.05] text-nanda-bg">{tt("Construisons ensemble.", "Let us build together.", "Construamos juntos.")}</h2>
 <p className="mt-8 max-w-xl text-nanda-bg/70 text-base md:text-[17px] leading-relaxed">
 {tt(
 "Partenariats, distribution, presse, recrutement, nos équipes sont à votre écoute, entre la Guinée-Bissau.",
 "Partnerships, distribution, press, recruitment, our teams are at your service, between Guinea-Bissau.",
 "Parcerias, distribuição, imprensa, recrutamento, as nossas equipas estão à sua escuta, entre a Guiné-Bissau."
 )}
 </p>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-5 flex md:justify-end items-end">
 <Link to="/contact" data-testid="home-cta-contact" className="btn-arrow inline-flex items-center gap-3 bg-nanda-bg text-nanda-ink px-7 py-4 hover:bg-nanda-sand transition-colors duration-500 label-overline">
 {tt("Nous contacter", "Contact us", "Contactar-nos")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 </div>
 </section>
 );
};

const Sectors = () => {
 const tt = useT();
 const SECTORS = [
 { icon: Sun, label: tt("Énergies renouvelables", "Renewable Energy", "Energias renováveis") },
 { icon: UtensilsCrossed, label: tt("Épicerie fine & Gastronomie", "Fine Foods & Gourmet Products", "Mercearia fina & Gastronomia") },
 { icon: Factory, label: tt("Transformation alimentaire", "Food Processing", "Transformação alimentar") },
 { icon: ShoppingBag, label: tt("Épicerie premium", "Premium Grocery", "Mercearia premium") },
 { icon: Nut, label: tt("Anacarde (Cashew)", "Cashew", "Caju") },
 { icon: Apple, label: tt("Fruits tropicaux", "Tropical Fruits & Fruit Products", "Frutos tropicais") },
 { icon: Droplet, label: tt("Miel", "Honey", "Mel") },
 { icon: Waves, label: tt("Sel naturel", "Natural Salt", "Sal natural") },
 { icon: Flame, label: tt("Épices & Condiments", "Spices & Condiments", "Especiarias & Condimentos") },
 { icon: Sparkles, label: tt("Cosmétiques naturels", "Natural Cosmetics", "Cosméticos naturais") },
 { icon: GraduationCap, label: tt("Éducation & Formation", "Education", "Educação") },
 { icon: HomeIcon, label: tt("Art de vivre africain", "African Lifestyle", "Arte de viver africana") },
 ];
 return (
 <section className="border-t border-nanda-line" data-testid="home-sectors">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-20">
 <FadeUp className="md:col-span-4">
 <div className="label-overline text-nanda-muted">{tt("Nos Secteurs", "Our Sectors", "Os nossos Sectores")}</div>
 <Link to="/secteurs" data-testid="home-sectors-link" className="btn-arrow mt-6 inline-flex items-center gap-2 label-overline text-nanda-ink hover:text-nanda-terracotta transition-colors">
 {tt("Explorer les 8 secteurs", "Explore all 8 sectors", "Explorar os 8 sectores")}
 <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-8">
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-balance leading-[1.05]">
 {tt("Une entreprise ouest-africaine, plurielle par nature.", "A West African company, plural by nature.", "Uma empresa oeste-africana, plural por natureza.")}
 </h2>
 <p className="mt-8 max-w-2xl text-nanda-inkSoft text-base md:text-lg leading-relaxed">
 {tt(
 "NANDA SÁ développe des produits premium et des solutions durables enracinés en Afrique de l'Ouest, pour les marchés locaux et internationaux.",
 "NANDA SÁ develops premium products and sustainable solutions rooted in West Africa, for local and international markets.",
 "A NANDA SÁ desenvolve produtos premium e soluções sustentáveis enraizados na África Ocidental, para os mercados locais e internacionais."
 )}
 </p>
 </FadeUp>
 </div>

 <Stagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {SECTORS.map((s, i) => {
 const Icon = s.icon;
 return (
 <StaggerItem key={i} className="bg-nanda-bg p-6 md:p-8 group hover:bg-nanda-bgAlt transition-colors duration-500" data-testid={`sector-${i}`}>
 <div className="text-nanda-ink group-hover:text-nanda-terracotta transition-colors duration-500 mb-5">
 <Icon size={24} strokeWidth={1.2} />
 </div>
 <div className="label-overline text-nanda-muted mb-2">0{i + 1 < 10 ? `0${i + 1}` : i + 1}</div>
 <div className="font-serif text-lg md:text-xl leading-snug text-nanda-ink">{s.label}</div>
 </StaggerItem>
 );
 })}
 </Stagger>
 </div>
 </section>
 );
};

/* ------------------------------------------------------------------ */
/*  CosmetiquesFeature — Home section presenting NANDA SÁ Cosmétiques */
/* ------------------------------------------------------------------ */
const CosmetiquesFeature = () => {
 const tt = useT();
 return (
 <section className="relative py-24 md:py-32 bg-[#F7F3EC] border-t border-nanda-line" data-testid="home-cosmetiques">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
 {/* Text column */}
 <FadeUp className="lg:col-span-6 order-2 lg:order-1">
 <div className="label-overline text-[#B8935E] mb-5">
 {tt("Cosmétiques NANDA SÁ", "NANDA SÁ Cosmetics", "Cosméticos NANDA SÁ")}
 </div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-nanda-ink leading-[1.05] tracking-tight">
 {tt(
 "Les trésors botaniques de la Guinée-Bissau, en rituel quotidien.",
 "The botanical treasures of Guinea-Bissau, as a daily ritual.",
 "Os tesouros botânicos da Guiné-Bissau, num ritual quotidiano."
 )}
 </h2>
 <p className="mt-6 md:mt-8 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed max-w-xl">
 {tt(
 "Karité, baobab, moringa, hibiscus, aloe vera, touloucouna, miel forestier : une gamme entière née de nos filières bissau-guinéennes, transformée avec la patience du rituel et l'exigence du luxe naturel.",
 "Shea, baobab, moringa, hibiscus, aloe vera, touloucouna, forest honey: a full collection born from our Bissau-Guinean value chains, transformed with the patience of ritual and the exacting standard of natural luxury.",
 "Karité, baobá, moringa, hibisco, aloé vera, toulucuna, mel florestal: uma gama inteira nascida das nossas fileiras bissau-guineenses, transformada com a paciência do ritual e a exigência do luxo natural."
 )}
 </p>
 <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:text-[15px] text-nanda-ink max-w-lg">
 {[
 tt("Chantilly de Karité & Coco", "Shea & Coconut Whipped Butter", "Chantilly de Karité & Coco"),
 tt("Beurre de Karité brut", "Raw Shea Butter", "Manteiga de Karité em bruto"),
 tt("Gel d'Aloe Vera", "Aloe Vera Gel", "Gel de Aloé Vera"),
 tt("Huiles végétales de Guinée-Bissau", "Guinea-Bissau plant oils", "Óleos vegetais da Guiné-Bissau"),
 tt("Laits corporels", "Body milks", "Leites corporais"),
 tt("Savons artisanaux", "Artisanal soaps", "Sabonetes artesanais"),
 tt("Gommages sensoriels", "Sensory scrubs", "Esfoliantes sensoriais"),
 tt("Amenity Kits sur-mesure", "Bespoke Amenity Kits", "Amenity Kits à medida"),
 ].map((label, i) => (
 <li key={i} className="flex items-center gap-2.5" data-testid={`cosmetique-highlight-${i}`}>
 <span className="w-1.5 h-1.5 rounded-full bg-[#B8935E] shrink-0" />
 <span>{label}</span>
 </li>
 ))}
 </ul>
 <div className="mt-10 flex flex-wrap gap-4 items-center">
 <Link
 to="/cosmetiques"
 data-testid="home-cosmetiques-cta"
 className="group inline-flex items-center gap-2.5 bg-nanda-ink text-nanda-bg px-7 py-3.5 hover:bg-[#B8935E] transition-colors duration-500 label-overline"
 >
 {tt("Découvrir la collection", "Discover the collection", "Descobrir a colecção")}
 <ArrowUpRight size={14} strokeWidth={1.5} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
 </Link>
 <Link
 to="/carnet"
 data-testid="home-cosmetiques-carnet"
 className="link-underline label-overline text-nanda-inkSoft hover:text-nanda-ink transition-colors"
 >
 {tt("Lire le Carnet des ingrédients", "Read the ingredient Journal", "Ler o Caderno de ingredientes")}
 </Link>
 </div>
 </FadeUp>

 {/* Image mosaic column */}
 <FadeUp delay={0.2} className="lg:col-span-6 order-1 lg:order-2">
 <div className="grid grid-cols-6 gap-3 md:gap-4">
 <div className="col-span-4 aspect-[4/5] overflow-hidden bg-nanda-bg">
 <img
 src="/photos/nanda-cosmetiques/hibiscus-oil.png"
 alt={tt(
 "Huile d'Hibiscus NANDA SÁ · Guinée-Bissau",
 "NANDA SÁ Hibiscus Oil · Guinea-Bissau",
 "Óleo de Hibisco NANDA SÁ · Guiné-Bissau"
 )}
 className="w-full h-full object-cover"
 loading="lazy"
 />
 </div>
 <div className="col-span-2 grid grid-rows-2 gap-3 md:gap-4">
 <div className="aspect-square overflow-hidden bg-nanda-bg">
 <img
 src="/photos/nanda-cosmetiques/chantilly-karite-coco.png"
 alt={tt("Chantilly de Karité & Coco", "Shea & Coconut Whipped Butter", "Chantilly de Karité & Coco")}
 className="w-full h-full object-cover"
 loading="lazy"
 />
 </div>
 <div className="aspect-square overflow-hidden bg-nanda-bg">
 <img
 src="/photos/nanda-cosmetiques/baobab-oil.png"
 alt={tt("Huile de Baobab", "Baobab Oil", "Óleo de Baobá")}
 className="w-full h-full object-cover"
 loading="lazy"
 />
 </div>
 </div>
 </div>
 <div className="mt-5 label-overline text-nanda-muted tracking-wider">
 {tt(
 "Fabriqués en Guinée-Bissau · Distribués internationalement",
 "Made in Guinea-Bissau · Distributed internationally",
 "Fabricados na Guiné-Bissau · Distribuídos internacionalmente"
 )}
 </div>
 </FadeUp>
 </div>
 </section>
 );
};

export default function Home() {
 return (
 <div data-testid="page-home">
 <Hero />
 <B2BPillars />
 <Manifeste />
 <OpportunitesGB />
 <CosmetiquesFeature />
 <VisionMission />
 <Houses />
 <Sectors />
 <Stats />
 <Tresors />
 <Engagements />
 <Why />
 <Carnet />
 <Testimonials />
 <B2BQuickAccess />
 <CTA />
 </div>
 );
}
